import { useMemo, useState } from 'react';
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { fuzzyFilter } from '@/util';
import { GalleryFilterSortByType } from '@/type';

const DEFAULT_SORT_BY: GalleryFilterSortByType = 'uploadDate';

export const useImageGalleryTable = <TData extends BaseTableItem>(
  data: TData[] = [],
  columns: ColumnDef<TData, any>[],
  defaultSelectedItem?: number,
) => {
  const [selectedImageId, setSelectedImageId] = useState<number | undefined>(
    defaultSelectedItem,
  );
  const [filter, setFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<GalleryFilterSortByType | undefined>(
    DEFAULT_SORT_BY,
  );
  const [sorting, setSorting] = useState<SortingState>([
    { id: DEFAULT_SORT_BY, desc: true },
  ]);

  const columnFilters = useMemo(() => [{ id: 'name', value: filter }], [filter]);

  const table = useReactTable<TData>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    filterFns: { fuzzy: fuzzyFilter },
    state: { columnFilters, sorting },
    onSortingChange: setSorting,
    autoResetPageIndex: false,
  });

  const updateFilter = (filterText: string) => {
    setSelectedImageId(undefined);
    setFilter(filterText);
  };

  const updateSort = (field: GalleryFilterSortByType, dir: 'asc' | 'desc') => {
    setSortBy(field);
    setSorting([{ id: field, desc: dir === 'desc' }]);
  };

  const items = table.getSortedRowModel().rows.map((row) => ({
    id: Number(row.original.id),
    name: row.original.name,
    file: row.original.file,
    width: row.original.width,
    height: row.original.height,
    uploadDate: row.original.uploadDate,
  }));

  return {
    selectedImageId,
    setSelectedImageId,
    filter,
    updateFilter,
    sortBy,
    sorting,
    updateSort,
    table,
    items,
  };
};

type BaseTableItem = {
  id: number;
  name: string;
  file: string;
  width: number;
  height: number;
  uploadDate?: string;
};
